/**
 * FornitureAccordiPage — Console operatore /forniture
 *
 * I FORNITORI sulla piattaforma (capitolo 166): chi ha la capability `fornitore_catalogo`,
 * se il profilo DAC7 è completo, l'ACCORDO con la piattaforma (canone, provvigione o misto,
 * esenzione dei clienti storici, minimo mensile, decorrenza) e il conto delle provvigioni
 * mese per mese. L'accordo lo scrive SOLO il super utente, qui: ogni modifica conserva la
 * versione precedente nello storico, perché il conto di un mese usa la versione in vigore.
 * In fondo, l'export CSV per la comunicazione DAC7 dell'anno.
 *
 * Dal 29/10/2026 (cap. 446) anche lo STATO del fornitore sulla piattaforma: in attesa, approvato, sospeso. Le aziende
 * lo vedono (merci, gare, accordi) solo da approvato. Lo scrive il server (`fornitureConsoleStatoFornitore`), qui si
 * legge e si cambia coi bottoni.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DownloadOutlined from '@mui/icons-material/DownloadOutlined';
import Refresh from '@mui/icons-material/Refresh';
import { collection, doc, getDoc, getDocs, query, serverTimestamp, setDoc, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useTranslation } from 'react-i18next';
import { db, functions } from '../firebase';
import { RACCOLTA_FORNITORI_SULLA_PIATTAFORMA, statoDelFornitore, type FornitoreSullaPiattaformaDoc, type StatoDelFornitore, type StatoScrittoDelFornitore } from '@shared/forniture/fornitoreSullaPiattaforma';
import { SoftChip } from '../components/SoftChip';
import { listTenants, type TenantConsoleListItem } from '../services/tenantService';
import type { AccordoPiattaforma, ModelloAccordo, ProfiloFornitore, ProvvigioniMese, VersioneAccordo } from '@shared/forniture/tipi';
import { MODELLI_ACCORDO } from '@shared/forniture/tipi';

const CAPABILITY = 'fornitore_catalogo';
const ETICHETTA_MODELLO: Record<ModelloAccordo, string> = { canone: 'canone', provvigione: 'provvigione', misto: 'misto' };

interface RigaFornitore {
  tenant: TenantConsoleListItem;
  /** Lo stato sulla piattaforma (cap. 446): documento assente = in attesa. */
  piattaforma: { stato: StatoDelFornitore; deciso_il: string | null; motivo: string | null };
  profilo: ProfiloFornitore | null;
  accordo: AccordoPiattaforma | null;
  provvigioni: ProvvigioniMese[];
}

function haCapability(t: TenantConsoleListItem): boolean {
  const m = t.modules as { base?: string[]; addon?: string[]; expired?: string[]; override?: Record<string, boolean> } | undefined;
  if (!m) return false;
  if (m.override && CAPABILITY in m.override) return m.override[CAPABILITY] === true;
  return (m.addon ?? []).includes(CAPABILITY) || (m.base ?? []).includes(CAPABILITY);
}

const euro = (n: number) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(n);

async function caricaFornitori(): Promise<RigaFornitore[]> {
  const tenants = (await listTenants({ max: 500 })).filter(haCapability);
  return Promise.all(tenants.map(async (tenant) => {
    const [p, a, prov, s] = await Promise.all([
      getDoc(doc(db, 'forniture_profili', tenant.id)),
      getDoc(doc(db, 'forniture_accordi_piattaforma', tenant.id)),
      getDocs(query(collection(db, 'forniture_provvigioni'), where('fornitore_tenant_id', '==', tenant.id))),
      getDoc(doc(db, RACCOLTA_FORNITORI_SULLA_PIATTAFORMA, tenant.id)),
    ]);
    const sp = s.exists() ? (s.data() as FornitoreSullaPiattaformaDoc) : null;
    return {
      tenant,
      piattaforma: { stato: statoDelFornitore(sp), deciso_il: sp?.deciso_il ?? null, motivo: sp?.motivo ?? null },
      profilo: p.exists() ? ({ id: p.id, ...(p.data() as Omit<ProfiloFornitore, 'id'>) }) : null,
      accordo: a.exists() ? ({ id: a.id, ...(a.data() as Omit<AccordoPiattaforma, 'id' | 'storico'>), storico: ((a.data() as { storico?: VersioneAccordo[] }).storico ?? []) }) : null,
      provvigioni: prov.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<ProvvigioniMese, 'id'>) })).sort((x, y) => y.mese.localeCompare(x.mese)),
    };
  }));
}

/** Cambia lo stato del fornitore sulla piattaforma (server: rilegge e risponde con lo stato che vale dopo). */
async function cambiaStatoFornitore(fornitore: string, stato: StatoScrittoDelFornitore, motivo?: string): Promise<StatoDelFornitore> {
  const inizio = performance.now();
  try {
    const r = await httpsCallable<{ fornitore_tenant_id: string; stato: StatoScrittoDelFornitore; motivo?: string }, { stato: StatoDelFornitore }>(functions, 'fornitureConsoleStatoFornitore')({ fornitore_tenant_id: fornitore, stato, motivo });
    console.info(`[console/forniture] fornitureConsoleStatoFornitore ok in ${Math.round(performance.now() - inizio)} ms`, { fornitore, stato: r.data.stato });
    return r.data.stato;
  } catch (err) {
    console.error('[console/forniture] fornitureConsoleStatoFornitore fallita', { fornitore, stato, err });
    throw err;
  }
}

const TONO_STATO: Record<StatoDelFornitore, 'warning' | 'success' | 'error'> = { in_attesa: 'warning', approvato: 'success', sospeso: 'error' };

/** La sospensione chiede il perché: resta nella storia del fornitore. */
const SospendiDialog: React.FC<{ riga: RigaFornitore; onChiudi: () => void; onConferma: (motivo: string) => Promise<void> }> = ({ riga, onChiudi, onConferma }) => {
  const { t } = useTranslation('fornitori');
  const [motivo, setMotivo] = useState('');
  const [lavora, setLavora] = useState(false);
  return (
    <Dialog open onClose={lavora ? undefined : onChiudi} maxWidth="sm" fullWidth>
      <DialogTitle>{t('dialogo_sospendi.titolo', { fornitore: riga.profilo?.denominazione || riga.tenant.nome_azienda })}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t('dialogo_sospendi.testo')}</Typography>
        <TextField label={t('dialogo_sospendi.motivo')} helperText={t('dialogo_sospendi.motivo_aiuto')} value={motivo} onChange={(e) => setMotivo(e.target.value)} multiline minRows={3} fullWidth required autoFocus />
      </DialogContent>
      <DialogActions>
        <Button onClick={onChiudi} disabled={lavora}>{t('dialogo_sospendi.annulla')}</Button>
        <Button
          variant="contained"
          color="error"
          disabled={lavora || !motivo.trim()}
          onClick={() => { setLavora(true); void onConferma(motivo.trim()).finally(() => setLavora(false)); }}
        >
          {t('dialogo_sospendi.conferma')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/** Salva una NUOVA versione dell'accordo: la precedente finisce nello storico. Rilegge. */
async function salvaAccordo(tenantId: string, versione: VersioneAccordo, precedente: AccordoPiattaforma | null): Promise<AccordoPiattaforma> {
  const ref = doc(db, 'forniture_accordi_piattaforma', tenantId);
  const storico: VersioneAccordo[] = precedente ? [...(precedente.storico ?? []), { modello: precedente.modello, canone_mensile_eur: precedente.canone_mensile_eur, provvigione_percentuale: precedente.provvigione_percentuale, esenzione_clienti_storici: precedente.esenzione_clienti_storici, minimo_mensile_eur: precedente.minimo_mensile_eur ?? null, valido_da: precedente.valido_da, stripe_price_id: precedente.stripe_price_id ?? null, note: precedente.note ?? null }] : [];
  await setDoc(ref, { ...versione, id: tenantId, storico, updated_at: serverTimestamp(), ...(precedente ? {} : { created_at: serverTimestamp() }) }, { merge: true });
  const riletto = await getDoc(ref);
  if (!riletto.exists()) throw new Error('L’accordo non risulta salvato');
  const d = riletto.data() as Omit<AccordoPiattaforma, 'id'>;
  if (d.modello !== versione.modello || d.provvigione_percentuale !== versione.provvigione_percentuale || d.canone_mensile_eur !== versione.canone_mensile_eur) throw new Error('L’accordo riletto non corrisponde a quello scritto');
  return { id: tenantId, ...d };
}

const AccordoDialog: React.FC<{ riga: RigaFornitore; onChiudi: () => void; onSalvato: (a: AccordoPiattaforma) => void; onErrore: (m: string) => void }> = ({ riga, onChiudi, onSalvato, onErrore }) => {
  const a = riga.accordo;
  const [f, setF] = useState({
    modello: (a?.modello ?? 'misto') as ModelloAccordo,
    canone: String(a?.canone_mensile_eur ?? 0),
    provvigione: String(a?.provvigione_percentuale ?? 3),
    esenzione: a?.esenzione_clienti_storici ?? true,
    minimo: a?.minimo_mensile_eur != null ? String(a.minimo_mensile_eur) : '',
    validoDa: a?.valido_da ?? new Date().toISOString().slice(0, 10),
    stripe: a?.stripe_price_id ?? '',
    note: a?.note ?? '',
  });
  const [lavorando, setLavorando] = useState(false);
  const num = (v: string) => Number(v.replace(',', '.'));
  const valido = /^\d{4}-\d{2}-\d{2}$/.test(f.validoDa) && Number.isFinite(num(f.canone)) && Number.isFinite(num(f.provvigione)) && num(f.provvigione) >= 0 && num(f.provvigione) <= 100;
  const salva = async () => {
    setLavorando(true);
    try {
      const v: VersioneAccordo = {
        modello: f.modello,
        canone_mensile_eur: f.modello === 'provvigione' ? 0 : num(f.canone),
        provvigione_percentuale: f.modello === 'canone' ? 0 : num(f.provvigione),
        esenzione_clienti_storici: f.esenzione,
        minimo_mensile_eur: f.minimo.trim() ? num(f.minimo) : null,
        valido_da: f.validoDa,
        stripe_price_id: f.stripe.trim() || null,
        note: f.note.trim() || null,
      };
      onSalvato(await salvaAccordo(riga.tenant.id, v, a));
      onChiudi();
    } catch (e) {
      console.error('[console/forniture] accordo non salvato:', e);
      onErrore(e instanceof Error ? e.message : 'Salvataggio fallito');
    } finally {
      setLavorando(false);
    }
  };
  return (
    <Dialog open onClose={() => !lavorando && onChiudi()} fullWidth maxWidth="sm">
      <DialogTitle>Accordo con {riga.tenant.nome_azienda}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">Ogni salvataggio crea una nuova versione con la sua decorrenza; la precedente resta nello storico e il conto di ogni mese usa la versione in vigore in quel mese.</Typography>
          <TextField select label="Modello" size="small" value={f.modello} onChange={(e) => setF((p) => ({ ...p, modello: e.target.value as ModelloAccordo }))}>
            {MODELLI_ACCORDO.map((m) => <MenuItem key={m} value={m}>{ETICHETTA_MODELLO[m]}</MenuItem>)}
          </TextField>
          <Stack direction="row" spacing={2}>
            <TextField label="Canone mensile (€)" size="small" fullWidth value={f.canone} onChange={(e) => setF((p) => ({ ...p, canone: e.target.value }))} disabled={f.modello === 'provvigione'} />
            <TextField label="Provvigione (%)" size="small" fullWidth value={f.provvigione} onChange={(e) => setF((p) => ({ ...p, provvigione: e.target.value }))} disabled={f.modello === 'canone'} />
            <TextField label="Minimo mensile (€)" size="small" fullWidth value={f.minimo} onChange={(e) => setF((p) => ({ ...p, minimo: e.target.value }))} />
          </Stack>
          <FormControlLabel control={<Checkbox checked={f.esenzione} onChange={(e) => setF((p) => ({ ...p, esenzione: e.target.checked }))} />} label="Clienti storici esenti dalla provvigione nelle gare (180 giorni dall’ultimo ordine); sulle merci del cantiere si paga sempre" />
          <Stack direction="row" spacing={2}>
            <TextField label="In vigore dal" type="date" size="small" value={f.validoDa} onChange={(e) => setF((p) => ({ ...p, validoDa: e.target.value }))} InputLabelProps={{ shrink: true }} />
            <TextField label="Stripe price id (ondata 6, facoltativo)" size="small" fullWidth value={f.stripe} onChange={(e) => setF((p) => ({ ...p, stripe: e.target.value }))} />
          </Stack>
          <TextField label="Note" size="small" fullWidth multiline minRows={2} value={f.note} onChange={(e) => setF((p) => ({ ...p, note: e.target.value }))} />
          {a && a.storico?.length > 0 && (
            <Box>
              <Typography variant="subtitle2">Versioni precedenti</Typography>
              {a.storico.map((v, i) => <Typography key={i} variant="caption" display="block" color="text.secondary">dal {v.valido_da}: {ETICHETTA_MODELLO[v.modello]} · canone {euro(v.canone_mensile_eur)} · provvigione {v.provvigione_percentuale}%{v.esenzione_clienti_storici ? ' · storici esenti nelle gare' : ''}</Typography>)}
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onChiudi} disabled={lavorando}>Annulla</Button>
        <Button variant="contained" onClick={salva} disabled={!valido || lavorando}>{lavorando ? <CircularProgress size={18} sx={{ color: 'inherit' }} /> : 'Salva la versione'}</Button>
      </DialogActions>
    </Dialog>
  );
};

/** Il CSV DAC7 dell'anno: un rigo per fornitore con i dati identificativi e i corrispettivi. */
function csvDac7(righe: RigaFornitore[], anno: number): string {
  const q = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const intestazione = ['tenant_id', 'denominazione', 'partita_iva', 'codice_fiscale', 'identificativo_fiscale', 'iban', 'via', 'cap', 'citta', 'provincia', 'nazione', 'anno', 'ordini', 'corrispettivi_imponibile_eur', 'provvigioni_eur', 'trimestre_1', 'trimestre_2', 'trimestre_3', 'trimestre_4'];
  const corpo = righe.map((r) => {
    const mesi = r.provvigioni.filter((p) => p.mese.startsWith(`${anno}-`));
    const trimestre = (n: number) => mesi.filter((p) => Math.ceil(Number(p.mese.slice(5, 7)) / 3) === n).reduce((s, p) => s + p.imponibile_totale_eur, 0);
    const p = r.profilo;
    return [r.tenant.id, p?.denominazione ?? r.tenant.nome_azienda, p?.partita_iva ?? '', p?.codice_fiscale ?? '', p?.identificativo_fiscale ?? '', p?.iban ?? '', p?.sede?.via ?? '', p?.sede?.cap ?? '', p?.sede?.citta ?? '', p?.sede?.provincia ?? '', p?.sede?.nazione ?? '', anno, mesi.reduce((s, m) => s + m.righe.length, 0), mesi.reduce((s, m) => s + m.imponibile_totale_eur, 0).toFixed(2), mesi.reduce((s, m) => s + m.provvigioni_eur, 0).toFixed(2), trimestre(1).toFixed(2), trimestre(2).toFixed(2), trimestre(3).toFixed(2), trimestre(4).toFixed(2)].map(q).join(';');
  });
  return [intestazione.join(';'), ...corpo].join('\r\n');
}

export const FornitureAccordiPage: React.FC = () => {
  const { t } = useTranslation('fornitori');
  const [righe, setRighe] = useState<RigaFornitore[]>([]);
  const [daSospendere, setDaSospendere] = useState<RigaFornitore | null>(null);
  const [cambiando, setCambiando] = useState<string | null>(null);
  const [caricamento, setCaricamento] = useState(true);
  const [errore, setErrore] = useState<string | null>(null);
  const [aperta, setAperta] = useState<RigaFornitore | null>(null);
  const [snack, setSnack] = useState<{ open: boolean; severity: 'success' | 'error'; message: string }>({ open: false, severity: 'success', message: '' });
  const [anno, setAnno] = useState(new Date().getFullYear());

  const carica = async () => {
    setCaricamento(true);
    setErrore(null);
    try { setRighe(await caricaFornitori()); }
    catch (e) { console.error('[console/forniture] caricamento fallito:', e); setErrore(e instanceof Error ? e.message : 'Caricamento fallito'); }
    finally { setCaricamento(false); }
  };
  useEffect(() => { void carica(); }, []);

  const cambiaStato = async (r: RigaFornitore, stato: StatoScrittoDelFornitore, motivo?: string) => {
    const nome = r.profilo?.denominazione || r.tenant.nome_azienda;
    setCambiando(r.tenant.id);
    try {
      const dopo = await cambiaStatoFornitore(r.tenant.id, stato, motivo);
      const oggi = new Date().toISOString().slice(0, 10);
      setRighe((prev) => prev.map((x) => (x.tenant.id === r.tenant.id ? { ...x, piattaforma: { stato: dopo, deciso_il: oggi, motivo: motivo ?? null } } : x)));
      setSnack({ open: true, severity: 'success', message: t(dopo === 'approvato' ? 'esito.approvato' : 'esito.sospeso', { fornitore: nome }) });
      setDaSospendere(null);
    } catch (e) {
      setSnack({ open: true, severity: 'error', message: t('esito.errore', { motivo: e instanceof Error ? e.message : String(e) }) });
    } finally {
      setCambiando(null);
    }
  };

  const totaleAnno = useMemo(() => righe.reduce((s, r) => s + r.provvigioni.filter((p) => p.mese.startsWith(`${anno}-`)).reduce((x, p) => x + p.totale_dovuto_eur, 0), 0), [righe, anno]);

  const scaricaCsv = () => {
    const blob = new Blob([`﻿${csvDac7(righe, anno)}`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dac7-fornitori-${anno}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} spacing={2} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Fornitori sulla piattaforma</Typography>
          <Typography variant="body2" color="text.secondary">Accordi (canone, provvigione o misto), profili DAC7 e il conto delle provvigioni. L’accordo si scrive solo qui.</Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField select size="small" label="Anno" value={anno} onChange={(e) => setAnno(Number(e.target.value))} sx={{ minWidth: 110 }}>
            {[0, 1, 2].map((d) => { const y = new Date().getFullYear() - d; return <MenuItem key={y} value={y}>{y}</MenuItem>; })}
          </TextField>
          <Button variant="outlined" startIcon={<DownloadOutlined />} onClick={scaricaCsv} disabled={!righe.length}>CSV DAC7 {anno}</Button>
          <Button variant="text" startIcon={<Refresh />} onClick={carica} disabled={caricamento}>Ricarica</Button>
        </Stack>
      </Stack>
      {errore && <Alert severity="error" sx={{ mb: 2 }}>{errore}</Alert>}
      {caricamento ? <CircularProgress /> : righe.length === 0 ? (
        <Alert severity="info">Nessun tenant ha la capability «fornitore_catalogo». Si attiva dal catalogo (scripts/attiva-capability-fornitore.cjs) o dal dettaglio del tenant.</Alert>
      ) : (
        <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 960 }}>
            <TableHead>
              <TableRow>
                <TableCell>Fornitore</TableCell>
                <TableCell>{t('stato.colonna')}</TableCell>
                <TableCell>Profilo DAC7</TableCell>
                <TableCell>Accordo</TableCell>
                <TableCell align="right">Dovuto {anno}</TableCell>
                <TableCell>Ultimi mesi</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {righe.map((r) => {
                const anno_ = r.provvigioni.filter((p) => p.mese.startsWith(`${anno}-`));
                return (
                  <TableRow key={r.tenant.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{r.profilo?.denominazione || r.tenant.nome_azienda}</Typography>
                      <Typography variant="caption" color="text.secondary">{r.tenant.id}{r.profilo?.partita_iva ? ` · P. IVA ${r.profilo.partita_iva}` : ''}{r.profilo?.regioni_servite?.length ? ` · ${r.profilo.regioni_servite.join(', ')}` : ''}</Typography>
                    </TableCell>
                    <TableCell>
                      <SoftChip label={t(`stato.${r.piattaforma.stato}`)} tone={TONO_STATO[r.piattaforma.stato]} />
                      <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5, maxWidth: 220 }}>
                        {r.piattaforma.stato === 'in_attesa' ? t('stato.spiega_in_attesa') : r.piattaforma.deciso_il ? t('stato.dal', { data: r.piattaforma.deciso_il.split('-').reverse().join('/') }) : ''}
                        {r.piattaforma.stato === 'sospeso' && r.piattaforma.motivo ? ` · ${r.piattaforma.motivo}` : ''}
                      </Typography>
                      <Box sx={{ mt: 0.75 }}>
                        {r.piattaforma.stato === 'approvato' ? (
                          <Button size="small" color="error" disabled={cambiando === r.tenant.id} onClick={() => setDaSospendere(r)} sx={{ textTransform: 'none' }}>{t('azioni.sospendi')}</Button>
                        ) : (
                          <Button size="small" variant="contained" disabled={cambiando === r.tenant.id} onClick={() => void cambiaStato(r, 'approvato')} sx={{ textTransform: 'none' }}>
                            {t(r.piattaforma.stato === 'sospeso' ? 'azioni.riattiva' : 'azioni.approva')}
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{r.profilo ? <SoftChip label={r.profilo.pronto_dac7 ? 'completo' : 'incompleto'} tone={r.profilo.pronto_dac7 ? 'success' : 'warning'} /> : <SoftChip label="assente" tone="error" />}</TableCell>
                    <TableCell>
                      {r.accordo ? (
                        <>
                          <SoftChip label={ETICHETTA_MODELLO[r.accordo.modello]} tone="primary" />
                          <Typography variant="caption" display="block" color="text.secondary">{r.accordo.modello !== 'provvigione' ? `canone ${euro(r.accordo.canone_mensile_eur)}` : ''}{r.accordo.modello === 'misto' ? ' · ' : ''}{r.accordo.modello !== 'canone' ? `${r.accordo.provvigione_percentuale}%` : ''}{r.accordo.esenzione_clienti_storici ? ' · storici esenti nelle gare' : ''} · dal {r.accordo.valido_da}</Typography>
                        </>
                      ) : <SoftChip label="da scrivere" tone="warning" />}
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{euro(anno_.reduce((s, p) => s + p.totale_dovuto_eur, 0))}</TableCell>
                    <TableCell>
                      {r.provvigioni.slice(0, 3).map((p) => <Typography key={p.id} variant="caption" display="block" color="text.secondary">{p.mese}: {p.righe.length} ordini · imponibile {euro(p.imponibile_totale_eur)} · dovuto {euro(p.totale_dovuto_eur)}</Typography>)}
                      {!r.provvigioni.length && <Typography variant="caption" color="text.disabled">nessun mese calcolato</Typography>}
                    </TableCell>
                    <TableCell align="right"><Button size="small" variant="outlined" onClick={() => setAperta(r)}>{r.accordo ? 'Nuova versione' : 'Scrivi l’accordo'}</Button></TableCell>
                  </TableRow>
                );
              })}
              <TableRow>
                <TableCell colSpan={4} sx={{ fontWeight: 700 }}>Totale dovuto {anno}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{euro(totaleAnno)}</TableCell>
                <TableCell colSpan={2} />
              </TableRow>
            </TableBody>
          </Table>
        </Paper>
      )}
      {aperta && <AccordoDialog riga={aperta} onChiudi={() => setAperta(null)} onSalvato={(a) => { setRighe((prev) => prev.map((r) => (r.tenant.id === a.id ? { ...r, accordo: a } : r))); setSnack({ open: true, severity: 'success', message: `Accordo di ${aperta.tenant.nome_azienda} salvato (in vigore dal ${a.valido_da}).` }); }} onErrore={(m) => setSnack({ open: true, severity: 'error', message: m })} />}
      {daSospendere && <SospendiDialog riga={daSospendere} onChiudi={() => setDaSospendere(null)} onConferma={(m) => cambiaStato(daSospendere, 'sospeso', m)} />}
      <Snackbar open={snack.open} autoHideDuration={4500} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
        <Alert severity={snack.severity} variant="filled" onClose={() => setSnack((s) => ({ ...s, open: false }))}>{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default FornitureAccordiPage;
